import { Base58Util } from "@solomon-labs/base58";
import { useEffect, useRef, useState } from "react";

export interface MultisigPreflightResponse {
    /** `resolving` means the backend kicked off a detached vault → multisig
     *  directory scan (~50s cold); re-issue the same preflight after a short
     *  delay — it answers from the DB map once the scan lands. */
    status: "ok" | "not-found" | "lookup-failed" | "vault-unknown-to-system" | "unsupported-version" | "resolving";
    multisigAddress?: string;
    vaultAddress?: string | null;
    programId?: string;
    resolvedFrom?: "multisig" | "vault";
    memberCount?: number;
    threshold?: number;
    signerStatus?: "can-verify" | "cannot-verify" | "not-member" | "no-signer";
    /** Every member of the multisig with whether they can verify (holds Vote
     *  or Execute permission). Present on `status: "ok"`. Lets the console show
     *  the signer list and derive any connected wallet's membership without a
     *  second preflight. */
    members?: { address: string; canVerify: boolean }[];
    /** Present when status === "unsupported-version" — e.g. "v3". */
    version?: string;
}

/**
 * Console-specific preflight transport. Each console instantiates the hook
 * with its own endpoint (client: /org/client/positions/multisig-preflight,
 * issuer: /org/issuer/program-positions/multisig-preflight); the response
 * contract is identical.
 */
export type SquadsPreflightFetch = (args: {
    address: string;
    signerAddress: string | null;
    signal?: AbortSignal;
}) => Promise<MultisigPreflightResponse>;

export interface SquadsMultisigMember {
    address: string;
    canVerify: boolean;
}

export interface SquadsMultisigInfo {
    address: string;
    vaultAddress?: string;
    programId: string;
    resolvedFrom: "multisig" | "vault";
    memberCount: number;
    threshold: number;
    /** Full signer list from the preflight. Absent when the backend predates
     *  the members field, in which case callers can fall back to a signer-
     *  scoped preflight for membership. */
    members?: SquadsMultisigMember[];
}

export type SquadsSignerStatus = "can-verify" | "cannot-verify" | "not-member" | "no-signer";

export type SquadsMultisigStatus =
    | { status: "idle" }
    | { status: "invalid" }
    | { status: "loading" }
    | { status: "not-found" }
    | { status: "vault-unknown-to-system" }
    | { status: "unsupported-version"; version: string }
    | { status: "lookup-failed"; error: string }
    | {
          status: "success";
          multisig: SquadsMultisigInfo;
          signerStatus: SquadsSignerStatus;
      };

export type SquadsCheckObserver = (cached: boolean) => (result: SquadsMultisigStatus) => void;
function observeCheck(
    observer: SquadsCheckObserver | undefined,
    cached: boolean
): (result: SquadsMultisigStatus) => void {
    let resultObserver: ((result: SquadsMultisigStatus) => void) | undefined;
    try {
        resultObserver = observer?.(cached);
    } catch {
        /* Analytics is optional. */
    }
    return (result): void => {
        try {
            resultObserver?.(result);
        } catch {
            /* Analytics is optional. */
        }
    };
}

export type UseSquadsMultisig = (
    address: string,
    signerAddress: string | null,
    /** Bump to force a fresh lookup of the *same* address (e.g. a "try again"
     *  after a lookup-failed). Transient failures aren't cached, so the effect
     *  re-runs the preflight. */
    retryNonce?: number,
    observer?: SquadsCheckObserver
) => SquadsMultisigStatus;

const DEBOUNCE_MS = 300;
/* Total budget for one logical preflight, INCLUDING `resolving` polls.
 * A cold vault paste makes the backend run a detached directory scan
 * (~115k accounts ≈ 30–60 s on Helius) while each poll returns fast with
 * `resolving`; 90 s gives the scan room and bounds a genuinely stuck
 * backend. Individual HTTP requests stay well under the API's 10 s
 * requestTimeout — never hold one connection across the scan. */
const PREFLIGHT_TIMEOUT_MS = 90_000;
/* Delay between `resolving` polls. Each poll is one cheap DB-map lookup
 * server-side, so this only trades resolution latency vs request count. */
const RESOLVING_POLL_INTERVAL_MS = 5_000;

/**
 * Per-console cache + in-flight dedupe, keyed by `(address, signer)`.
 *
 * Why this exists: the wallet connector polls connection state (~10s),
 * and that churn — plus any subtree remount — used to re-run the effect
 * below, whose cleanup aborted the in-flight preflight. A cold
 * reverse-lookup scan takes ~50s, so it never survived a full poll
 * cycle and the UI showed `lookup-failed` even though the backend
 * eventually resolved (and cached) the mapping.
 *
 * The cache survives re-renders AND remounts: concurrent callers for the
 * same key share one request, and a resolved result is reused instantly.
 * Transient miss/failure statuses are never cached so retry flows can
 * recover without a full reload.
 */
type PreflightCacheEntry =
    | { kind: "result"; value: SquadsMultisigStatus; expiresAt: number }
    | { kind: "pending"; promise: Promise<SquadsMultisigStatus> };

/* Resolved results expire so multisig membership changes (a signer added
 * or removed on Squads) surface within minutes instead of persisting for
 * the whole session. The on-chain assertMember at submit time is the
 * authority either way — this only keeps the UI gate honest. */
const PREFLIGHT_CACHE_TTL_MS = 5 * 60_000;

function preflightCacheKey(address: string, signerAddress: string | null): string {
    return `${address}|${signerAddress ?? ""}`;
}

export function isCacheablePreflightResult(result: SquadsMultisigStatus): boolean {
    return result.status === "success" || result.status === "unsupported-version";
}

/**
 * Issue the preflight, re-polling while the backend reports `resolving`
 * (a detached vault → multisig directory scan is running server-side).
 * Resolves with the first settled response; rejects with an AbortError
 * when `signal` fires mid-request or mid-wait.
 *
 * Exported for tests; the factory-created hook owns the abort/timeout wiring.
 */
export async function pollPreflightUntilSettled(args: {
    fetchPreflight: () => Promise<MultisigPreflightResponse>;
    signal: AbortSignal;
    pollIntervalMs: number;
}): Promise<MultisigPreflightResponse> {
    while (true) {
        const response = await args.fetchPreflight();
        if (response.status !== "resolving") {
            return response;
        }
        await sleepUnlessAborted(args.pollIntervalMs, args.signal);
    }
}

function sleepUnlessAborted(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
        }
        const onAbort = (): void => {
            clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
        };
        const timer = setTimeout(() => {
            signal.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        signal.addEventListener("abort", onAbort, { once: true });
    });
}

export function extractSquadsMultisigAddress(input: string): string {
    const trimmed = input.trim();
    if (!trimmed) return "";

    try {
        const url = new URL(trimmed);
        const segments = url.pathname.split("/").filter(Boolean);
        const squadsSegmentIndex = segments.findIndex((segment) => segment.toLowerCase() === "squads");
        const candidate = squadsSegmentIndex >= 0 ? segments[squadsSegmentIndex + 1] : null;
        if (candidate) {
            return candidate;
        }
    } catch {
        // Not a URL; treat the input as a raw public key below.
    }

    return trimmed;
}

/**
 * Build the console's `useSquadsMultisig` hook around its preflight
 * transport. The debounce/poll/cache machinery is identical across
 * consoles; only the endpoint differs.
 */
export function createUseSquadsMultisig(fetchPreflight: SquadsPreflightFetch): UseSquadsMultisig {
    const preflightCache = new Map<string, PreflightCacheEntry>();

    function readCachedPreflightResult(key: string): SquadsMultisigStatus | null {
        const cached = preflightCache.get(key);
        if (cached?.kind !== "result") return null;
        if (Date.now() > cached.expiresAt) {
            preflightCache.delete(key);
            return null;
        }
        return cached.value;
    }

    async function runPreflight(
        address: string,
        signerAddress: string | null,
        key: string
    ): Promise<SquadsMultisigStatus> {
        // Hard max-duration bound only — NOT tied to React re-renders, so
        // benign wallet-poll churn can no longer cancel a healthy request.
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), PREFLIGHT_TIMEOUT_MS);
        try {
            const response = await pollPreflightUntilSettled({
                fetchPreflight: () => fetchPreflight({ address, signerAddress, signal: controller.signal }),
                signal: controller.signal,
                pollIntervalMs: RESOLVING_POLL_INTERVAL_MS
            });
            const mapped = mapPreflightResponse(response);
            if (isCacheablePreflightResult(mapped)) {
                preflightCache.set(key, {
                    kind: "result",
                    value: mapped,
                    expiresAt: Date.now() + PREFLIGHT_CACHE_TTL_MS
                });
            } else {
                preflightCache.delete(key);
            }
            return mapped;
        } catch (err: unknown) {
            preflightCache.delete(key);
            const aborted = err instanceof DOMException && err.name === "AbortError";
            const message = aborted
                ? "Verification timed out. Please try again."
                : err instanceof Error
                  ? err.message
                  : "Couldn't reach the network.";
            return { status: "lookup-failed", error: message };
        } finally {
            window.clearTimeout(timer);
        }
    }

    return function useSquadsMultisig(
        address: string,
        signerAddress: string | null,
        retryNonce = 0,
        observer?: SquadsCheckObserver
    ): SquadsMultisigStatus {
        const trimmedAddress = extractSquadsMultisigAddress(address);
        const currentKey = preflightCacheKey(trimmedAddress, signerAddress);
        const [state, setState] = useState<{ key: string; value: SquadsMultisigStatus }>({
            key: currentKey,
            value: { status: "idle" }
        });
        const requestIdRef = useRef(0);
        const previousRetryNonce = useRef(retryNonce);
        const observerRef = useRef(observer);
        observerRef.current = observer;

        useEffect(() => {
            const trimmed = extractSquadsMultisigAddress(address);
            const key = preflightCacheKey(trimmed, signerAddress);
            if (previousRetryNonce.current !== retryNonce) {
                preflightCache.delete(key);
                previousRetryNonce.current = retryNonce;
            }
            const updateState = (value: SquadsMultisigStatus): void => setState({ key, value });
            if (trimmed.length === 0) {
                updateState({ status: "idle" });
                return;
            }

            if (!Base58Util.isValidPublicKey(trimmed)) {
                updateState({ status: "invalid" });
                return;
            }

            const requestId = ++requestIdRef.current;

            // Resolved result already cached (and not expired) → reuse
            // instantly, no network and no flash of "loading" on remount.
            const cachedResult = readCachedPreflightResult(key);
            if (cachedResult) {
                observeCheck(observerRef.current, true)(cachedResult);
                updateState(cachedResult);
                return;
            }

            updateState({ status: "loading" });
            let cancelled = false;

            const checkObserver = observerRef.current;
            const debounceTimer = window.setTimeout(() => {
                const completeCheck = observeCheck(checkObserver, false);
                const existing = preflightCache.get(key);
                const promise =
                    existing?.kind === "pending" ? existing.promise : runPreflight(trimmed, signerAddress, key);
                // Register the in-flight promise so concurrent callers (other
                // mounts, re-renders, wallet-poll churn) attach instead of
                // firing — and crucially, are never aborted by this effect's
                // cleanup.
                if (existing?.kind !== "pending") {
                    preflightCache.set(key, { kind: "pending", promise });
                }
                void promise.then((resolved) => {
                    completeCheck(resolved);
                    // Ignore if this hook instance moved on to a newer request.
                    if (cancelled || requestIdRef.current !== requestId) return;
                    updateState(resolved);
                });
            }, DEBOUNCE_MS);

            // Cleanup only drops THIS subscriber — the shared request keeps
            // running and populates the cache. No abort: re-renders and
            // remounts can no longer cancel an in-flight reverse-lookup.
            return (): void => {
                cancelled = true;
                window.clearTimeout(debounceTimer);
            };
        }, [address, signerAddress, retryNonce]);

        // Effects run after render. Never expose the previous destination or
        // signer's permission result during the first render after a change.
        if (!trimmedAddress) return { status: "idle" };
        if (!Base58Util.isValidPublicKey(trimmedAddress)) return { status: "invalid" };
        return state.key === currentKey ? state.value : { status: "loading" };
    };
}

export function mapPreflightResponse(response: MultisigPreflightResponse): SquadsMultisigStatus {
    if (response.status === "not-found") {
        return { status: "not-found" };
    }
    if (response.status === "vault-unknown-to-system") {
        return { status: "vault-unknown-to-system" };
    }
    if (response.status === "unsupported-version") {
        return { status: "unsupported-version", version: response.version ?? "" };
    }
    if (response.status === "lookup-failed") {
        return { status: "lookup-failed", error: "Couldn't reach the network." };
    }
    if (response.status === "resolving") {
        // Never reached via the hook — pollPreflightUntilSettled loops
        // until the backend settles. Mapped defensively for direct callers.
        return { status: "lookup-failed", error: "Vault resolution still in progress. Please try again." };
    }

    if (
        !response.multisigAddress ||
        !response.programId ||
        !response.resolvedFrom ||
        response.memberCount === undefined ||
        response.threshold === undefined ||
        !response.signerStatus
    ) {
        return { status: "lookup-failed", error: "Malformed multisig response." };
    }

    return {
        status: "success",
        multisig: {
            address: response.multisigAddress,
            ...(response.vaultAddress ? { vaultAddress: response.vaultAddress } : {}),
            programId: response.programId,
            resolvedFrom: response.resolvedFrom,
            memberCount: response.memberCount,
            threshold: response.threshold,
            ...(response.members !== undefined ? { members: response.members } : {})
        },
        signerStatus: response.signerStatus
    };
}

export const isVerifyMember = (status: SquadsMultisigStatus): boolean =>
    status.status === "success" && status.signerStatus === "can-verify";

export const isMemberCannotVerify = (status: SquadsMultisigStatus): boolean =>
    status.status === "success" && status.signerStatus === "cannot-verify";

/**
 * Derive a connected wallet's membership status from the resolved member
 * list — the same `canVerify` check the backend runs, mirrored client-side.
 *
 * This lets the paste-first flow resolve the multisig once (signer-less)
 * and report membership the instant a wallet connects, with no second
 * preflight. It is UX only: submit-time `assertMember` re-reads the
 * multisig on-chain and is the real authority.
 */
export function deriveSignerStatus(
    members: SquadsMultisigMember[],
    connectedAddress: string | null
): SquadsSignerStatus {
    if (!connectedAddress) return "no-signer";
    const member = members.find((candidate) => candidate.address === connectedAddress);
    if (!member) return "not-member";
    return member.canVerify ? "can-verify" : "cannot-verify";
}
